import type { ComponentChildren, JSX } from 'preact';

export function Page({ children, narrow = true }: { children: ComponentChildren; narrow?: boolean }): JSX.Element {
  return (
    <div className="min-h-screen flex flex-col">
      <header className="w-full border-b border-black/5 bg-white/70 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <a href="https://betteroo.ai" className="font-heading text-2xl font-bold text-brand-orange">
            Betteroo
          </a>
          <span className="text-sm text-gray-500">Gifting</span>
        </div>
      </header>
      <main className={`mx-auto w-full flex-1 px-4 py-6 ${narrow ? 'max-w-md' : 'max-w-5xl'}`}>{children}</main>
      <footer className="px-4 py-6 text-center text-xs text-gray-500">
        <a href="https://betteroo.ai/terms-of-service/" className="underline">
          Terms
        </a>{' '}
        ·{' '}
        <a href="https://betteroo.ai/privacy-policy/" className="underline">
          Privacy
        </a>{' '}
        · support@betteroo.ai
      </footer>
    </div>
  );
}

interface ButtonProps {
  onClick?: () => void;
  type?: 'button' | 'submit';
  children: ComponentChildren;
  disabled?: boolean;
  loading?: boolean;
  variant?: 'primary' | 'green' | 'ghost';
  className?: string;
}

export function Button({
  onClick,
  type = 'button',
  children,
  disabled = false,
  loading = false,
  variant = 'primary',
  className = '',
}: ButtonProps): JSX.Element {
  const base =
    'w-full rounded-xl px-6 py-4 text-base font-bold transition-all duration-200 flex items-center justify-center';
  const look =
    disabled || loading
      ? 'bg-[#EFEFF1] text-[#A3A5B2] cursor-not-allowed'
      : variant === 'green'
        ? 'bg-brand-green text-white hover:bg-brand-green-dark'
        : variant === 'ghost'
          ? 'bg-transparent text-gray-700 hover:bg-black/5'
          : 'bg-brand-orange text-white hover:bg-brand-orange-dark';
  return (
    <button type={type} onClick={onClick} disabled={disabled || loading} className={`${base} ${look} ${className}`}>
      {loading ? <Spinner /> : children}
    </button>
  );
}

export function Spinner(): JSX.Element {
  return (
    <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none" aria-label="Loading">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

interface FieldProps {
  label: string;
  htmlFor: string;
  hint?: string;
  error?: string | null;
  children: ComponentChildren;
}

export function Field({ label, htmlFor, hint, error, children }: FieldProps): JSX.Element {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-800">
        {label}
      </label>
      {children}
      {error ? <p className="text-sm text-red-600">{error}</p> : hint ? <p className="text-xs text-gray-500">{hint}</p> : null}
    </div>
  );
}

type InputProps = JSX.IntrinsicElements['input'] & { invalid?: boolean };

export function Input({ invalid = false, className = '', ...rest }: InputProps): JSX.Element {
  return (
    <input
      {...rest}
      className={`w-full rounded-lg border bg-white px-4 py-3 text-base text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-brand-orange focus:ring-1 focus:ring-brand-orange ${
        invalid ? 'border-red-400' : 'border-gray-300'
      } ${className}`}
    />
  );
}

type TextareaProps = JSX.IntrinsicElements['textarea'] & { invalid?: boolean };

export function Textarea({ invalid = false, className = '', ...rest }: TextareaProps): JSX.Element {
  return (
    <textarea
      {...rest}
      className={`w-full rounded-lg border bg-white px-4 py-3 text-base text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-brand-orange focus:ring-1 focus:ring-brand-orange ${
        invalid ? 'border-red-400' : 'border-gray-300'
      } ${className}`}
    />
  );
}

export function Card({ children, className = '' }: { children: ComponentChildren; className?: string }): JSX.Element {
  return <div className={`rounded-2xl bg-white p-5 shadow-sm ring-1 ring-black/5 ${className}`}>{children}</div>;
}

export function Notice({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'error' | 'success';
  children: ComponentChildren;
}): JSX.Element {
  const look =
    tone === 'error'
      ? 'bg-red-50 text-red-800 ring-red-200'
      : tone === 'success'
        ? 'bg-green-50 text-green-800 ring-green-200'
        : 'bg-brand-orange-light text-brand-orange-dark ring-brand-orange/20';
  return <div className={`rounded-xl px-4 py-3 text-sm ring-1 ${look}`}>{children}</div>;
}

export function Progress({ value }: { value: number }): JSX.Element {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200" aria-hidden="true">
      <div className="h-full rounded-full bg-brand-orange transition-all duration-300" style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
    </div>
  );
}

interface OptionProps {
  id: string;
  name: string;
  label: string;
  emoji?: string;
  selected: boolean;
  multi?: boolean;
  onSelect: () => void;
}

export function Option({ id, name, label, emoji, selected, multi = false, onSelect }: OptionProps): JSX.Element {
  const inputId = `${name}-${id}`;
  return (
    <div className="relative">
      <input
        type={multi ? 'checkbox' : 'radio'}
        name={name}
        id={inputId}
        value={id}
        className="sr-only"
        checked={selected}
        onChange={onSelect}
      />
      <label
        htmlFor={inputId}
        className={`flex cursor-pointer items-center justify-between gap-3 rounded-lg border p-4 transition-all ${
          selected ? 'border-brand-orange/60 bg-brand-orange/10' : 'border-gray-200 bg-white'
        }`}
      >
        <span className="flex items-center gap-3">
          {emoji ? <span className="text-xl leading-none">{emoji}</span> : null}
          <span className="font-medium">{label}</span>
        </span>
        <span
          className={`flex h-6 w-6 items-center justify-center rounded-full ${
            selected ? 'bg-brand-orange text-white' : 'border border-gray-200 bg-gray-100'
          }`}
          aria-hidden="true"
        >
          {selected ? '✓' : ''}
        </span>
      </label>
    </div>
  );
}
